import { type FunctionComponent } from 'react';
import { MAX_DEFAULT_INVENTORY_QUANTITY, MIN_INVENTORY_QUANTITY } from '../../../constants/restricted-inventory';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { ClickableElement, StyledButton } from '../../buttons';
import MinusIcon from '../../svgs/untitled-ui/minus.svg';
import PlusIcon from '../../svgs/untitled-ui/plus.svg';
import TrashIcon from '../../svgs/untitled-ui/trash.svg';
import { TextInput } from '../input/text-input/text-input.component';
import { userSelectNone } from './quantity-input.css';

const COUNT_BUTTON_STYLE = 'w-third flex items-center justify-center pointer theme-luxury:b-1! theme-luxury:rounded-full! ';

export type QuantityInputEventType = 'INCREMENT' | 'DECREMENT' | 'CHANGE' | 'BLUR' | 'ENTER';

type Size = 'SMALL' | 'DEFAULT' | 'LARGE';

type SizeStyle = {
	input: string;
	button: string;
	container?: string;
	icon?: string;
};

type QuantityInputStyle = {
	background: string;
	color: string;
	hover: string;
	buttons: string;
	container: string;
	input: string;
	disabledButton: string;
};

const DEFAULT_STYLE: QuantityInputStyle = {
	background: '',
	color: '',
	hover: '',
	buttons: '',
	container: '',
	input: '',
	disabledButton: ''
};

export const QUANTITY_INPUT_STYLE_MAP: Record<string, QuantityInputStyle> = {
	DEFAULT: {
		...DEFAULT_STYLE,
		color: 'theme-grey-darker',
		hover: 'hover-bg-theme-grey-light',
		buttons:
			'theme-luxury:text-brand-secondary! theme-luxury:focus:bg-white! theme-luxury:fg-brand-secondary theme-luxury:hover:bg-brand-primary! theme-luxury:active:bg-brand-secondary! theme-luxury:focus-visible:shadow-focused theme-luxury:border! theme-luxury:border-white!',
		container: 'theme-luxury:focus-within:shadow-focused theme-luxury:border-none! theme-luxury:br-100! theme-luxury:shadow-border',
		disabledButton:
			'theme-luxury:pointer-events-none theme-luxury:opacity-50 theme-luxury:text-disabled-subtle! theme-luxury:fg-disabled-subtle! theme-luxury:bg-disabled-subtle!'
	},
	PRIMARY: {
		...DEFAULT_STYLE,
		background: 'bg-theme-primary theme-luxury:bg-inherit!',
		color: 'theme-white',
		buttons:
			'theme-luxury:text-brand-secondary! theme-luxury:focus:bg-secondary-300! theme-luxury:fg-brand-secondary theme-luxury:hover:bg-brand-primary! theme-luxury:active:bg-brand-secondary! theme-luxury:focus-visible:shadow-focused theme-luxury:border! theme-luxury:border-white!',

		hover: 'hover-bg-theme-primary-darker'
	},
	DISABLED: {
		...DEFAULT_STYLE,
		container: 'theme-luxury:bg-disabled',
		input: 'bg-inherit text-gray-500 theme-luxury:text-disabled!',
		buttons: 'theme-luxury:text-disabled-subtle! theme-luxury:fg-disabled-subtle!'
	},
	ERROR: {
		...DEFAULT_STYLE,
		buttons:
			'theme-luxury:text-tertiary-400! theme-luxury:fg-brand-secondary theme-luxury:border-1 theme-luxury:border-white! theme-luxury:hover:bg-brand-primary! theme-luxury:active:bg-brand-secondary!',
		container: 'shadow-error focus-within:shadow-error-focused! border-0!',
		disabledButton:
			'theme-luxury:pointer-events-none theme-luxury:opacity-50 theme-luxury:text-disabled-subtle! theme-luxury:fg-disabled-subtle! theme-luxury:bg-disabled-subtle!'
	}
};

const SIZE_MAP: Record<Size, SizeStyle> = {
	SMALL: {
		input: 'input-small f6',
		button: 'input-small theme-luxury:w-[32px]!',
		container: 'theme-luxury:h-[32px]! theme-luxury:min-w-34',
		icon: 'theme-luxury:text-base!'
	},
	DEFAULT: {
		input: '',
		button: 'theme-luxury:w-[40px]!',
		container: 'theme-luxury:h-[40px]! theme-luxury:min-w-38',
		icon: 'theme-luxury:text-xl!'
	},
	LARGE: {
		input: 'input-large f4',
		button: 'input-large button-large theme-luxury:w-[56px]!',
		container: 'theme-luxury:h-[56px]! theme-luxury:min-w-46',
		icon: 'theme-luxury:text-xl!'
	}
};

type QuantityStyle = keyof typeof QUANTITY_INPUT_STYLE_MAP;

function calculateStyle(style: QuantityStyle, isError?: boolean, isDisabled?: boolean): QuantityInputStyle {
	if (isError) {
		return QUANTITY_INPUT_STYLE_MAP.ERROR;
	} else if (isDisabled) {
		return QUANTITY_INPUT_STYLE_MAP.DISABLED;
	} else {
		return QUANTITY_INPUT_STYLE_MAP[style];
	}
}

export type QuantityInputProps = {
	automationHook?: string;
	label?: string;
	minQuantity?: number;
	maxQuantity?: number;
	defaultQuantity?: number; // Quantity to default to when quantity is invalid on blur
	quantity: number | undefined;
	setQuantity: (quantity: number | undefined, event: QuantityInputEventType) => void | React.Dispatch<React.SetStateAction<number>>;
	disabled?: boolean;
	style?: QuantityStyle;
	canDelete?: boolean;
	isSettled?: boolean;
	isError?: boolean;
	size?: Size;
};

export const QuantityInput: FunctionComponent<QuantityInputProps> = ({
	automationHook,
	label = 'quantity input',
	minQuantity = MIN_INVENTORY_QUANTITY,
	defaultQuantity = MIN_INVENTORY_QUANTITY,
	quantity,
	setQuantity,
	disabled,
	style = 'DEFAULT',
	canDelete = false,
	isSettled,
	isError,
	size = 'DEFAULT',
	maxQuantity = MAX_DEFAULT_INVENTORY_QUANTITY
}) => {
	const quantityNumber = Number(quantity);
	const chosenStyle = calculateStyle(style, isError, disabled);
	// #region Event Handlers
	const incrementQuantity = () => {
		if (quantityNumber >= maxQuantity) {
			return setQuantity(maxQuantity, 'INCREMENT');
		}
		setQuantity(quantityNumber + 1, 'INCREMENT');
	};

	const decrementQuantity = () => {
		if (quantityNumber <= minQuantity) {
			return setQuantity(minQuantity, 'DECREMENT');
		}
		setQuantity(quantityNumber - 1, 'DECREMENT');
	};
	const decrementDisabled = (quantityNumber === minQuantity && !canDelete) || disabled;
	const incrementDisabled = quantityNumber === maxQuantity || disabled;
	const handleQuantityBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		// When we lose focus on the field and the value is invalid default it to 1
		setQuantity(!isNaN(value) ? Math.min(value, maxQuantity) : defaultQuantity, 'BLUR');
	};

	const handleQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
		// need to cache this quantity since setState is async another option is
		// using event.persist() could risk browser compatibility
		event.preventDefault();
		let newValue = Math.abs(parseInt(event.target.value, 10));
		if (!isNaN(newValue)) {
			// value should not be larger than MAX_QUANTITY
			newValue = Math.min(newValue, maxQuantity);
			// value should not be smaller than minQuantity
			newValue = Math.max(newValue, minQuantity);
			setQuantity(newValue, 'CHANGE');
		} else {
			// if value cannot parse an int set quantity to undefined so users can still enter single digit number 1-9
			setQuantity(undefined, 'CHANGE');
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		if (event.key === 'Enter') {
			setQuantity(quantity, 'ENTER');
		} else if (event.key === 'ArrowUp') {
			incrementQuantity();
		} else if (event.key === 'ArrowDown') {
			decrementQuantity();
		}
	};
	// #endregion

	const { input: inputClass, button: buttonClass, container: containerClass, icon: iconClass } = SIZE_MAP[size];

	return (
		<div
			className={`flex ba br2 justify-between theme-grey-light ${
				quantityNumber > 0 ? '' : isSettled ? 'ba b--theme-error' : ''
			} theme-luxury:rounded-full! ${chosenStyle.container} ${containerClass} theme-luxury:bg-primary`}>
			<ClickableElement
				ariaLabel="decrease quantity"
				className={`br2 br--left ${COUNT_BUTTON_STYLE} ${chosenStyle.color} ${chosenStyle.background} ${buttonClass} ${
					chosenStyle.hover
				} ${userSelectNone} theme-luxury:focus:outline-none! ${
					decrementDisabled ? chosenStyle.disabledButton : chosenStyle.buttons
				}`}
				onClick={decrementQuantity}
				onKeyDown={handleKeys(['Enter', ' '], decrementQuantity)}
				testId="decrease-quantity"
				automationHook={automationHook ? `${automationHook}-decrease` : undefined}
				disabled={disabled}>
				{canDelete && quantity === 1 ? (
					<TrashIcon className={`f3 ${iconClass}`} data-testid="TrashIcon" />
				) : (
					<MinusIcon className={`f3 ${iconClass}`} />
				)}
			</ClickableElement>
			<div className="w-third theme-luxury:w-18! flex items-center theme-luxury:ph3!">
				<TextInput
					ariaLabel={label}
					testId="input-quantity"
					omitBaseClasses={true}
					className={`input-reset bn f5 h-100 flex items-center ${chosenStyle.background} theme-grey-darker ${
						quantityNumber > 0 ? '' : 'outline-0'
					}`}
					inputClassName={`tc theme-luxury:focus:outline-none! ${chosenStyle.input} ${inputClass}`}
					value={quantity ?? ''}
					onBlur={handleQuantityBlur}
					onChange={handleQuantity}
					onKeyDown={handleKeys(['Enter', 'ArrowUp', 'ArrowDown'], handleKeyDown)}
					automationHook={automationHook}
					inputMode="numeric" // bring up numeric keyboard
					pattern="\d*" // REGEX: any number; by defining this pattern older iOS devices will bring up a numeric keyboard
					disabled={disabled}
				/>
			</div>
			<ClickableElement
				ariaLabel="increase quantity"
				className={`br2 br--right ${chosenStyle.color} ${chosenStyle.background} ${buttonClass} ${
					chosenStyle.hover
				} ${COUNT_BUTTON_STYLE} ${userSelectNone} theme-luxury:focus:outline-none! ${chosenStyle.buttons} ${
					incrementDisabled ? chosenStyle.disabledButton : ''
				}`}
				onClick={incrementQuantity}
				onKeyDown={handleKeys(['Enter', ' '], incrementQuantity)}
				testId="increase-quantity"
				automationHook={automationHook ? `${automationHook}-increase` : undefined}
				disabled={disabled}>
				<PlusIcon className={`f3 ${iconClass}`} data-testid="PlusIcon" />
			</ClickableElement>
		</div>
	);
};

export type DynamicQuantityInputProps = {
	text: string;
} & QuantityInputProps;

export const DynamicQuantityInput: FunctionComponent<DynamicQuantityInputProps> = (props) => {
	const containerStyle: React.CSSProperties = { width: '150px', height: '46px' };

	return (
		<>
			{props.quantity === 0 ? (
				<div style={containerStyle}>
					<StyledButton
						ariaLabel="open quantity selector"
						disabled={props.disabled}
						buttonStyle="SECONDARY"
						onClick={() => props.setQuantity(Number(props.quantity) + 1, 'INCREMENT')}>
						{props.text}
					</StyledButton>
				</div>
			) : (
				<div style={containerStyle}>
					<QuantityInput {...props} minQuantity={0} />
				</div>
			)}
		</>
	);
};
